import { useState, useEffect, useRef } from "react";
import styles from "./KingsCourt.module.css";
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';

const ROLES = [
  { name: "Rajavu", emoji: "👑", points: 20 },
  { name: "Rani", emoji: "👸", points: 18 },
  { name: "Mantri", emoji: "🧙", points: 16 },
  { name: "Police", emoji: "🛡️", points: 15 },
  { name: "Kallan", emoji: "🗡️", points: 12, zero: true },
  { name: "Bhadan", emoji: "⚔️", points: 11 },
  { name: "Thozhi", emoji: "🌺", points: 10 },
  { name: "Dasi", emoji: "🌿", points: 9 },
  { name: "Kavalalkaran", emoji: "🏹", points: 8 },
  { name: "Dhoodhan", emoji: "📜", points: 7 },
  { name: "Paachakakkaran", emoji: "🍲", points: 7 },
  { name: "Vyapari", emoji: "💰", points: 6 },
  { name: "Karshakan", emoji: "🌾", points: 6 },
  { name: "Vaidyan", emoji: "🌿", points: 5 },
  { name: "Nayathipathi", emoji: "⚖️", points: 5 },
  { name: "Vidushakan", emoji: "🎭", points: 4 },
];


const TECH = [
  {
    icon: "⚛️",
    name: "React + Vite",
    desc: "Component-driven UI with blazing-fast HMR. Vite's build pipeline kept development cycles snappy even as the codebase grew.",
    tag: "Frontend",
  },
  {
    icon: "🔥",
    name: "Firebase Realtime DB",
    desc: "WebSocket-based sync ensures every player sees state changes instantly — no polling, no lag, pure real-time magic.",
    tag: "Backend",
  },
  {
    icon: "🎨",
    name: "CSS Modules",
    desc: "Scoped styles prevented class collisions across game phases. Each component owned its visual identity cleanly.",
    tag: "Styling",
  },
];

const CHALLENGES = [
  {
    challenge: "Real-time state synchronization across 16 players without conflicts or race conditions.",
    solution:
      "Leveraged Firebase Realtime Database transactions for atomic updates. Designed a single source-of-truth game state object with role assignments separated from player lists.",
  },
  {
    challenge: "Role reshuffling on wrong guesses had to be seamless — players couldn't see each other's new roles during transition.",
    solution:
      "Implemented a server-side shuffle function triggered via Firebase Cloud Functions, sending masked role data per player. Each client only ever receives its own role assignment.",
  },
  {
    challenge: "Supporting 3–16 players with a dynamic role chain meant the identification order had to adapt at runtime.",
    solution:
      "Built a role-chain resolver that computes valid declaration sequences based on current player count. Edge cases like 3-player mode (Rajavu → Police skip) were handled with conditional chain logic.",
  },
  {
    challenge: "Host-controlled game flow while keeping non-host players reactive but not passive.",
    solution:
      "Separated host privileges into a permission layer. Non-host players receive live broadcast events and can act within their turn scope, but phase transitions require host confirmation.",
  },
  {
    challenge: "Designing a fair scoring system that rewards both high-rank players and clever low-rank plays.",
    solution:
      "Calibrated point values across 16 roles with a steep curve at the top (Rajavu 20 → Rani 18) and a catch penalty for Kallan (0 pts if caught), creating meaningful strategic tension.",
  },
];

const FLOW = [
  { icon: "🏰", title: "Lobby", desc: "Host creates a game and shares the 6-digit code. Players join from any device — no account needed." },
  { icon: "📜", title: "Scroll Selection", desc: "Each player secretly picks a scroll (paper). The scroll reveals their role privately — no one else sees." },
  { icon: "🔍", title: "Role Identification Chain", desc: "The chain begins: Rajavu searches for Rani, Rani finds Police, Police hunts Kallan. Wrong guesses reshuffle everything." },
  { icon: "📣", title: "Live Broadcast", desc: "When a role is successfully identified, it's broadcast to all players in real-time — tension builds with every reveal." },
  { icon: "🏆", title: "Score Exchange", desc: "Points are distributed based on roles held. Caught Kallan earns zero — the ultimate shame." },
  { icon: "🎯", title: "Game Over", desc: "Final scores displayed. Host can restart a fresh round or return to the lobby for a new session." },
];

const FEATURES = [
  { icon: "⚡", title: "Real-Time Multiplayer", desc: "Firebase WebSocket sync — every action broadcasts instantly to all connected players." },
  { icon: "🎲", title: "3–16 Players", desc: "Scales from intimate groups to full court gatherings with dynamic role assignment." },
  { icon: "🔄", title: "Wrong Guess Penalty", desc: "A failed identification reshuffles all roles — keeping every round unpredictable." },
  { icon: "📡", title: "Live Broadcasts", desc: "Role discoveries are announced to the entire court in real-time." },
  { icon: "👑", title: "Host Controls", desc: "The host manages game flow, timing, and can restart rounds seamlessly." },
  { icon: "📊", title: "Score System", desc: "Points tied to royal hierarchy — the higher your rank, the greater the reward." },
];

export default function KingsCourt() {
  const revealRefs = useRef([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.12 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addReveal = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.pageWrap}>
      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroBg} />
        <span className={styles.crownIcon}>♛</span>
        <p className={styles.heroSubtitle}>A Royal Deduction Experience</p>
        <h1 className={styles.heroTitle}>Kings Court</h1>
        <p className={styles.heroTagline}>
          Secrets, betrayal, and royal intrigue — in every round.
        </p>
        <div className={styles.heroDivider} />
        <div className={styles.heroBadges}>
          {["Multiplayer", "3–16 Players", "Real-Time", "Role-Based", "Kerala Inspired"].map((b) => (
            <span key={b} className={styles.badge}>{b}</span>
          ))}
        </div>
        
      </section>

      {/* ── OVERVIEW ── */}
      <div className={styles.overviewWrap}>
        <div className={styles.section}>
          <div className={styles.overviewGrid}>
            <div ref={addReveal} className={styles.reveal}>
              <p className={styles.sectionLabel}>Overview</p>
              <h2 className={styles.sectionTitle}>
                The <span className={styles.accent}>Court Awaits</span>
              </h2>
              <p className={styles.sectionDesc}>
                Kings Court is a real-time multiplayer role-based deduction game
                built for 3–16 players, rooted in the spirit of traditional
                Indian card games. Players are secretly assigned royal court
                roles and must identify each other through a tense chain of
                declarations — all while hiding their own identity.
              </p>
            </div>
            <div ref={addReveal} className={`${styles.reveal} ${styles.overviewStats}`}>
              {[
                { num: "16", label: "Unique Roles" },
                { num: "3–16", label: "Players" },
                { num: "∞", label: "Rounds" },
                { num: "20", label: "Max Points" },
              ].map((s) => (
                <div key={s.label} className={styles.statCard}>
                  <div className={styles.statNum}>{s.num}</div>
                  <div className={styles.statLabel}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── TECH STACK ── */}
      <div className={styles.techWrap}>
        <div className={styles.section}>
          <div ref={addReveal} className={styles.reveal}>
            <p className={styles.sectionLabel}>Tech Stack</p>
            <h2 className={styles.sectionTitle}>
              Built <span className={styles.accent}>With Purpose</span>
            </h2>
            <p className={styles.sectionDesc}>
              Every technology in this stack was chosen deliberately — not for
              trend, but for the specific demands of a real-time multiplayer
              game with no server backend.
            </p>
          </div>
          <div className={styles.techGrid}>
            {TECH.map((t, i) => (
              <div
                key={t.name}
                ref={addReveal}
                className={`${styles.techCard} ${styles.reveal}`}
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <span className={styles.techIcon}>{t.icon}</span>
                <div className={styles.techName}>{t.name}</div>
                <p className={styles.techDesc}>{t.desc}</p>
                <span className={styles.techTag}>{t.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CHALLENGES & SOLUTIONS ── */}
      <div className={styles.challengesWrap}>
        <div className={styles.section}>
          <div ref={addReveal} className={styles.reveal}>
            <p className={styles.sectionLabel}>Dev Journey</p>
            <h2 className={styles.sectionTitle}>
              Challenges <span className={styles.accent}>& Solutions</span>
            </h2>
            <p className={styles.sectionDesc}>
              Building a multiplayer deduction game from scratch surfaced
              problems I didn't anticipate — here's how each one was solved.
            </p>
          </div>
          <div className={styles.challengeList}>
            {CHALLENGES.map((c, i) => (
              <div
                key={i}
                ref={addReveal}
                className={`${styles.challengeItem} ${styles.reveal}`}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className={styles.challengeNum}>0{i + 1}</div>
                <div className={`${styles.challengeBox} ${styles.problem}`}>
                  <h3>⚠ Challenge</h3>
                  <p>{c.challenge}</p>
                </div>
                <div className={`${styles.challengeBox} ${styles.solution}`}>
                  <h3>✦ Solution</h3>
                  <p>{c.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div className={styles.howWrap}>
        <div className={styles.section}>
          <div ref={addReveal} className={styles.reveal}>
            <p className={styles.sectionLabel}>Game Flow</p>
            <h2 className={styles.sectionTitle}>
              How the <span className={styles.accent}>Game Runs</span>
            </h2>
            <p className={styles.sectionDesc}>
              From lobby to the final reveal — a complete round of Kings Court
              flows through six distinct phases.
            </p>
          </div>
          <div className={styles.flowChain}>
            {FLOW.map((step, i) => (
              <div
                key={i}
                ref={addReveal}
                className={`${styles.flowStep} ${styles.reveal}`}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className={styles.flowLeft}>
                  <div className={styles.flowDot}>{step.icon}</div>
                  {i < FLOW.length - 1 && <div className={styles.flowLine} />}
                </div>
                <div className={styles.flowContent}>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ROLES ── */}
      <div className={styles.rolesWrap}>
        <div className={styles.section}>
          <div ref={addReveal} className={styles.reveal}>
            <p className={styles.sectionLabel}>The Court</p>
            <h2 className={styles.sectionTitle}>
              16 <span className={styles.accent}>Royal Roles</span>
            </h2>
            <p className={styles.sectionDesc}>
              Every role carries a rank, a score, and a secret agenda. The court
              is never the same twice.
            </p>
          </div>
          <div className={styles.rolesGrid}>
            {ROLES.map((r, i) => (
              <div
                key={r.name}
                ref={addReveal}
                className={`${styles.roleCard} ${styles.reveal}`}
                style={{ transitionDelay: `${(i % 8) * 0.06}s` }}
              >
                <span className={styles.roleEmoji}>{r.emoji}</span>
                <span className={styles.roleName}>{r.name}</span>
                <span className={`${styles.rolePoints}${r.zero ? ` ${styles.zero}` : ""}`}>
                  {r.zero ? "0 if caught" : `${r.points} pts`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* ── SCREENSHOTS ── */}
      <div className={styles.ssSection}>
        <div>
          <p className={styles.ssEyebrow}>Screenshots</p>
          <h2 className={styles.ssTitle}>Game in <em>action</em></h2>
          <p className={styles.ssSub}>A look at the lobby, gameplay, and leaderboard screens.</p>
        </div>
        <div className={styles.ssGrid}>
          {[
            { img: require('../../../images/king-2.png'), label: 'Game Lobby — Waiting for Players' },
            { img: require('../../../images/king-3.png'), label: 'Active Round — Role Reveal' },
            { img: require('../../../images/king-4.png'), label: 'Leaderboard — End of Round' },
            { img: require('../../../images/king-5.png'), label: 'Role Cards — Full Hierarchy' },
            { img: require('../../../images/king-6.png'), label: 'Accusation Phase — Guess the Thief' },
            { img: require('../../../images/king-7.png'), label: 'Score Summary — Round Results' },
            { img: require('../../../images/king-8.png'), label: 'Role Cards — Full Hierarchy' },
            { img: require('../../../images/king-9.png'), label: 'Accusation Phase — Guess the Thief' },
            { img: require('../../../images/king-10.png'), label: 'Score Summary — Round Results' },
          ].map((s, i) => (
            <div key={i} className={styles.ssCard} style={{ animationDelay: `${i * 0.08}s` }}>
              <div className={styles.ssImgWrap}>
                <img src={s.img} alt={s.label} className={styles.ssImg} />

              </div>
              {/* <div className={styles.ssLabel}>{s.label}</div> */}
            </div>
          ))}
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className={styles.footer}>
        <div className={styles.footerLogo}>Kings Court</div>
        <div className={styles.footerLine}>Built with React · Firebase · CSS Modules</div>
        <a href="https://thief-and-police.vercel.app" target="_blank" rel="noreferrer" className={styles.footerLink}>
          Play Now ♛
        </a>
      </footer>
      </div>
      <Footer />
    </>
  );
}